import { useEffect, useState } from "react";
import { Box, Grid, IconButton, Stack } from "@mui/material";
import ByzCard from "../ByzCard";
import { ByzFilterCardProps } from "./types";
import OfficesFilter from "./filters/OfficesFilter";
import DateRangeFilter from "./filters/DateRangeFilter";
import StatusFilter from "./filters/StatusFilter";
import FloorFilter from "./filters/FloorFilter";
import DepartmentFilter from "./filters/DepartmentFilter";
import ComingTypeFilter from "./filters/ComingTypeFilter";
import BuildingFilter from "./filters/BuildingFilter";
import ByzMuiButton from "../mui/buttons/ByzMuiButton";
import { useTranslate } from "src/locales";

// Icon paths
const ICON_BASE_PATH = "/assets/icons/components/byzfilter";

export default function ByzFilterCard({
  title,
  children,
  filters = [
    "offices",
    "dateRange",
    "status",
    "floors",
    "departments",
    "comingTypes",
  ],
  onChange,
  onFilter,
  onClear,
  onPrint,
  onDownload,
  onSave,
  statusTypeId,
  statusOptions,
  variant = "default",
}: ByzFilterCardProps) {
  // state'ler
  const [office, setOffice] = useState<any[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [status, setStatus] = useState<any[]>([{ label: "Gönderildi", value: "1" }]);
  const [floorId, setFloorId] = useState<number>();
  const [departmentId, setDepartmentId] = useState<number>();
  const [buildingId, setBuildingId] = useState<number>();
  const [comingType, setComingType] = useState<any[]>([]);
  const { t } = useTranslate();

  useEffect(() => {
    onChange?.({
      office,
      startDate,
      endDate,
      status,
      floorId,
      departmentId,
      buildingId,
      comingType,
    });
  }, [
    office,
    startDate,
    endDate,
    status,
    floorId,
    departmentId,
    buildingId,
    comingType,
    onChange,
  ]);

  const resetFilters = () => {
    setOffice([]);
    setStartDate(new Date());
    setEndDate(new Date());
    setStatus([{ label: "Gönderildi", value: "1" }]);
    setFloorId(undefined);
    setDepartmentId(undefined);
    setBuildingId(undefined);
    setComingType([]);
  };

  const renderFilter = (field: string) => {
          switch (field) {
            case "offices":
              return <OfficesFilter key="offices" onChange={setOffice} />;
            case "dateRange":
              return (
                <DateRangeFilter
                  key="dateRange"
                  startDate={startDate}
                  endDate={endDate}
                  onStartChange={setStartDate}
                  onEndChange={setEndDate}
                />
              );
            case "status":
              return (
                <StatusFilter
                  key="status"
                  value={status}
                  onChange={setStatus}
                  typeId={statusTypeId ?? 0}
            customOptions={statusOptions}
                />
              );
            case "floors":
              return (
                <FloorFilter
                  key="floors"
                  onChange={(val) => {
                    // val null ise undefined, aksi halde id
                    setFloorId(val ? val.id : undefined);
                  }}
                  buildingId={buildingId}
                />
              );
            case "departments":
              return (
                <DepartmentFilter
                  key="departments"
            onChange={(val) => setDepartmentId(val?.id)}
                />
              );
            case "comingTypes":
              return (
                <ComingTypeFilter
                  key="comingTypes"
                  value={comingType}
                  onChange={setComingType}
                />
              );
      case "buildings":
        return (
          <BuildingFilter
            key="buildings"
            onChange={(val) => {
              setBuildingId(val?.id);
              // Building değiştiğinde floor'u sıfırla
              setFloorId(undefined);
            }}
          />
        );
            default:
              return null;
          }
  };

  // Default variant butonları
  const renderDefaultButtons = () => (
    <Stack direction="row" spacing={2}>
        <ByzMuiButton
          variant="contained"
          color="primary"
        onClick={() => onFilter?.()}
        >
          {t("grid-buttons.filter")}
        </ByzMuiButton>
        <ByzMuiButton
          variant="outlined"
          onClick={() => {
            resetFilters();
            onClear?.();
          }}
        >
          {t("grid-buttons.clear")}
        </ByzMuiButton>
      </Stack>
  );

  // Icon variant butonları
  const renderIconButtons = () => (
    <Stack direction="row" spacing={1} alignItems="center">
      {/* Search/Filter Button */}
      <IconButton
        onClick={() => onFilter?.()}
        sx={{ p: 0 }}
      >
        <Box
          component="img"
          src={`${ICON_BASE_PATH}/search-button.svg`}
          alt="Search"
          sx={{ width: 70, height: 70 }}
        />
      </IconButton>

      {/* Divider Line */}
      <Box
        component="img"
        src={`${ICON_BASE_PATH}/line.svg`}
        alt="Divider"
        sx={{ height: 50, mx: 1 }}
      />

      {/* Print Button */}
      <IconButton
        disabled={true}
        onClick={() => onPrint?.()}
        sx={{ p: 0 }}
      >
        <Box
          component="img"
          src={`${ICON_BASE_PATH}/print-icon.svg`}
          alt="Print"
          sx={{ width: 50, height: 50 }}
        />
      </IconButton>

      {/* Download Button */}
      <IconButton
        disabled={true}
        onClick={() => onDownload?.()}
        sx={{ p: 0 }}
      >
        <Box
          component="img"
          src={`${ICON_BASE_PATH}/download-icon.svg`}
          alt="Download"
          sx={{ width: 50, height: 50 }}
        />
      </IconButton>

      {/* Save Button */}
      <IconButton
        disabled={true}
        onClick={() => onSave?.()}
        sx={{ p: 0 }}
      >
        <Box
          component="img"
          src={`${ICON_BASE_PATH}/save-icon.svg`}
          alt="Save"
          sx={{ width: 50, height: 50 }}
        />
      </IconButton>
    </Stack>
  );

  return (
    <ByzCard title={title}>
      <Grid container spacing={2} alignItems="center">
        {/* Tüm filtreler */}
        {filters.map((field) => renderFilter(field))}
        
        {/* Butonlar - sağa hizalı, kalan alanı kapla */}
        <Grid 
          item 
          xs={12} 
          md 
          sx={{ 
            display: "flex", 
            justifyContent: { xs: "center", md: "flex-end" }, 
            mt: { xs: 2, md: 0 },
            flexGrow: 1
          }}
        >
          {variant === "icon" ? renderIconButtons() : renderDefaultButtons()}
        </Grid>
      </Grid>

      {children}
    </ByzCard>
  );
}
